import { ITEM_ID_PATTERN, MAX_RULES_NAME_CHARS } from "../rules/types.ts";
import { checkItem, checkStageHours, isRecord, readDrought, readHarvestAt, readLook, readRegions, readRegrow, readSeasons, readSeedBox, readSeedReturn, readStages, readYield, refuseUnknown } from "./crop-read.ts";
import { DEFAULT_DEAD_TINT, DEFAULT_WITHERED_TINT, MAX_CROPS, type CropContext, type CropDefinition, type CropProblem, type CropVerdict, type CropsVerdict } from "./crop-types.ts";
import crops from "./crops.json";

/**
 * Validation of crop documents (ADR-144 C1): `validateCrop` is the one function the world compile and the Crop editor share.
 * Pure.
 */

export * from "./crop-types.ts";

const CROP_FIELDS = ["id", "name", "seed", "seedBox", "stages", "harvestAt", "withered", "dead", "drought", "seasons", "regions", "yield", "seedReturn", "regrow"];

/**
 * The crops the game ships, from `crops.json`: the wheat and pumpkin of ADR-144 C3 and C4. It is the Crop editor's seed
 * document, not behaviour; a published world carries its own `crops.json`.
 */
export const DEFAULT_CROPS: readonly CropDefinition[] = Object.freeze(crops as CropDefinition[]);

/**
 * Checks a crop document against ADR-144 C1 and fills its defaults.
 *
 * @param value One entry of `crops.json` as parsed.
 * @param context The items and frames the crop may use.
 * @returns The crop with every default filled in, or every problem with the path of its field.
 * @example
 * validateCrop(JSON.parse(text), { itemKind: (id) => items.get(id)?.kind, hasFrame: (frame) => frames.has(frame) });
 */
export function validateCrop(value: unknown, context: CropContext): CropVerdict {
  const problems: CropProblem[] = [];
  if (!isRecord(value)) return { ok: false, problems: [{ field: "", message: "a crop must be an object" }] };
  refuseUnknown(value, CROP_FIELDS, "", problems);
  if (typeof value.id !== "string" || !ITEM_ID_PATTERN.test(value.id)) problems.push({ field: "id", message: "must be a lowercase id of letters, digits and underscores" });
  if (typeof value.name !== "string" || value.name.length < 1 || value.name.length > MAX_RULES_NAME_CHARS) problems.push({ field: "name", message: `must be 1 to ${MAX_RULES_NAME_CHARS} characters` });
  checkItem(value.seed, "material", "seed", context, problems);
  const seedBox = readSeedBox(value.seedBox, context, problems);
  const stages = readStages(value.stages, context, problems);
  const ripe = stages === null ? null : readHarvestAt(value.harvestAt, stages.length, problems);
  if (stages !== null && ripe !== null) checkStageHours(stages, ripe, problems);
  const withered = readLook(value.withered, "withered", DEFAULT_WITHERED_TINT, context, problems);
  const dead = readLook(value.dead, "dead", DEFAULT_DEAD_TINT, context, problems);
  const drought = readDrought(value.drought, problems);
  const seasons = readSeasons(value.seasons, problems);
  const regions = readRegions(value.regions, problems);
  const yields = readYield(value.yield, context, problems);
  const seedReturn = readSeedReturn(value.seedReturn, problems);
  const regrow = readRegrow(value.regrow, ripe, problems);
  if (problems.length > 0 || stages === null || ripe === null || withered === null || dead === null || drought === null || seasons === null || regions === null || yields === null || seedReturn === null) {
    return { ok: false, problems };
  }
  const crop: CropDefinition = {
    id: value.id as string,
    name: value.name as string,
    seed: value.seed as string,
    ...(seedBox.box === undefined ? {} : { seedBox: seedBox.box }),
    stages,
    harvestAt: ripe,
    withered,
    dead,
    drought,
    seasons,
    regions,
    yield: yields,
    seedReturn,
    ...(regrow.regrow === undefined ? {} : { regrow: regrow.regrow })
  };
  return { ok: true, crop };
}

/**
 * Names the path of a problem inside the crop at an index of the document.
 *
 * @param index The crop's index.
 * @param field The problem's path inside the crop, or "" for the crop itself.
 * @returns The path in the document.
 */
function documentPath(index: number, field: string): string {
  return field === "" ? `[${index}]` : `[${index}].${field}`;
}

/**
 * Checks a whole crops document: every crop, and that no two share an id or a seed item.
 *
 * @param value The parsed `crops.json`, a list of crops in the order the wire indexes them.
 * @param context The items and frames the crops may use.
 * @returns The crops in file order, or every problem with the path of its field (`[1].seed`).
 */
export function validateCrops(value: unknown, context: CropContext): CropsVerdict {
  if (!Array.isArray(value)) return { ok: false, problems: [{ field: "", message: "the crops document must be a list" }] };
  if (value.length > MAX_CROPS) return { ok: false, problems: [{ field: "", message: `a world holds at most ${MAX_CROPS} crops` }] };
  const problems: CropProblem[] = [];
  const crops: CropDefinition[] = [];
  const ids = new Set<string>();
  const seeds = new Set<string>();
  value.forEach((entry: unknown, index) => {
    const verdict = validateCrop(entry, context);
    if (!verdict.ok) {
      for (const problem of verdict.problems) problems.push({ field: documentPath(index, problem.field), message: problem.message });
      return;
    }
    if (ids.has(verdict.crop.id)) problems.push({ field: documentPath(index, "id"), message: "is already a crop" });
    if (seeds.has(verdict.crop.seed)) problems.push({ field: documentPath(index, "seed"), message: "is already another crop's seed" });
    ids.add(verdict.crop.id);
    seeds.add(verdict.crop.seed);
    crops.push(verdict.crop);
  });
  return problems.length === 0 ? { ok: true, crops } : { ok: false, problems };
}
